/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  GoabFormItem,
  GoabInputNumber,
  GoabInputOnChangeDetail,
  GoabRadioGroup,
  GoabRadioGroupOnChangeDetail,
  GoabRadioItem,
  GoabTable,
  GoabText,
  GoaInputOnBlurDetail,
} from '@abgov/angular-components';
import { EmptyValueMaskPipe } from '@ala/common-ui';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import {
  ClassValidatorFormBuilderService,
  ClassValidatorFormControl,
  ClassValidatorFormGroup,
} from 'ngx-reactive-form-class-validator';
import { EmptyBedReasonsDto } from '../../../../dtos-v2/empty-reasons.dto';
import { FacilityBedRowDto } from '../../../../dtos-v2/facility-bed-row.dto';
import { FacilityBedEmptyReasonFormDTO } from '../../../../dtos-v2/forms/facility-bed-empty-reason.form.dto';
import { FacilityCapacityEntryStateService } from '../../../../services-v2/states/facility-capacity-entry.state.service';
import { FacilityEntryBedRowStateService } from '../../../../services-v2/states/facility-entry-bed-row.state.service';
import { BedTypeCode } from '../../../../types-v2/bed-type-code';
import { TFacilityEmptyBedReasonsForm } from '../../../../types-v2/forms/facility-empty-bed-reasons.form.type';
import { getErrors } from '../../../../validators-v2/base/error.resolver';
@Component({
  selector: 'app-facility-beds-entry-row',
  imports: [
    CommonModule,
    GoabFormItem,
    GoabRadioGroup,
    GoabRadioItem,
    GoabText,
    GoabTable,
    GoabInputNumber,
    EmptyValueMaskPipe,
    ReactiveFormsModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './facility-beds-entry-row.html',
  styleUrl: './facility-beds-entry-row.scss',
})
export class FacilityBedsEntryRow {
  bedType = input.required<string>();
  displayFor = input.required<string>();

  private readonly _facilityEntryBedRowStateService = inject(
    FacilityEntryBedRowStateService
  );
  private readonly _facilityEntryStateService = inject(
    FacilityCapacityEntryStateService
  );

  private _fb = inject(ClassValidatorFormBuilderService);
  private _destroyRef = inject(DestroyRef);

  facilityBeds = this._facilityEntryBedRowStateService.selectFacilityBedsRows;
  currentFacilityBed(): FacilityBedRowDto {
    return this.facilityBeds().filter((r) => r.bedType === this.bedType())[0];
  }

  formGroup: ClassValidatorFormGroup<TFacilityEmptyBedReasonsForm>;
  getErrors = getErrors;

  getControl(control: string): ClassValidatorFormControl {
    return this.formGroup.get(control) as ClassValidatorFormControl;
  }

  getValue(id: number) {
    return id ? id.toString() : '';
  }

  totalLastWeekInput = computed(() => {
    const currentFacilityBed = this.currentFacilityBed();
    if (!currentFacilityBed.emptyBedReasons) {
      return 0;
    }

    const values = [
      this.getLastWeekValue('openBedLessTwoWeekReferral'),
      this.getLastWeekValue('openBedTwoWeekNoReferral'),
      this.getLastWeekValue('bedMismatchPhysical'),
      this.getLastWeekValue('bedMismatchSmoking'),
      this.getLastWeekValue('bedMismatchPeerGender'),
      this.getLastWeekValue('bedMismatchEquipment'),
      this.getLastWeekValue('bedMismatchCognitive'),
      this.getLastWeekValue('bedMismatchClientRefusal'),
      this.getLastWeekValue('onHoldPendingOrMatched'),
      this.getLastWeekValue('tempCloseMaintenance'),
      this.getLastWeekValue('tempCloseOther'),
    ];

    return values.reduce(
      (sum: number, value: number | null) => sum + (value || 0),
      0
    );
  });

  totalThisWeekBedsEntry = computed(() => {
    const currentFacilityBed = this.currentFacilityBed();
    if (!currentFacilityBed.emptyBedReasons) {
      return 0;
    }

    const values = [
      this.getCurrentEmptyBedValue('openBedLessTwoWeekReferral'),
      this.getCurrentEmptyBedValue('openBedTwoWeekNoReferral'),
      this.getCurrentEmptyBedValue('bedMismatchPhysical'),
      this.getCurrentEmptyBedValue('bedMismatchSmoking'),
      this.getCurrentEmptyBedValue('bedMismatchPeerGender'),
      this.getCurrentEmptyBedValue('bedMismatchEquipment'),
      this.getCurrentEmptyBedValue('bedMismatchCognitive'),
      this.getCurrentEmptyBedValue('bedMismatchClientRefusal'),
      this.getCurrentEmptyBedValue('onHoldPendingOrMatched'),
      this.getCurrentEmptyBedValue('tempCloseMaintenance'),
      this.getCurrentEmptyBedValue('tempCloseOther'),
    ];

    return values.reduce(
      (sum: number, value: number | null) => sum + (value || 0),
      0
    );
  });

  constructor() {
    this.formGroup = this._fb.group(FacilityBedEmptyReasonFormDTO, {
      hasEmptyBeds: undefined,
      fundedBeds: undefined,
      openBedLessTwoWeekReferral: undefined,
      openBedTwoWeekNoReferral: undefined,
      bedMismatchPhysical: undefined,
      bedMismatchSmoking: undefined,
      bedMismatchPeerGender: undefined,
      bedMismatchEquipment: undefined,
      bedMismatchCognitive: undefined,
      bedMismatchClientRefusal: undefined,
      onHoldPendingOrMatched: undefined,
      tempCloseMaintenance: undefined,
      tempCloseOther: undefined,
    });

    this.formGroup.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((value) => {
        if (value && this.formGroup.touched) {
          this._facilityEntryStateService.setEntryChanged = true;
        }
      });

    // pull back the data from state service initially and attaching to form
    this._facilityEntryBedRowStateService.facilityBedsRows$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((state) => {
        this.patchFormFromState(state);
        this.formGroup.markAsUntouched();
      });
  }

  patchFormFromState(state: FacilityBedRowDto[]) {
    if (!state) return;

    const row = state.find((r) => r.bedType === this.bedType());
    if (!row) {
      this.formGroup.markAsUntouched();
      return;
    }

    // Base values
    const patch: any = {
      hasEmptyBeds: row.hasEmptyBeds ?? undefined,
      fundedBeds: row.fundedBedCount,
    };

    // Dynamically patch empty bed reasons
    const reasons = row.emptyBedReasons ?? ({} as EmptyBedReasonsDto);

    Object.entries(reasons).forEach(([reasonKey, reasonValue]) => {
      patch[reasonKey] = reasonValue.current;
    });

    this.formGroup.patchValue(patch);

    // Update validation state
    this._facilityEntryBedRowStateService.initializeValidationState();
    this._facilityEntryBedRowStateService.setBedRowValidation = {
      bedType: this.bedType(),
      isValid: this.formGroup.valid,
    };
  }

  getCurrentEmptyBedValue(reasonKey: keyof EmptyBedReasonsDto): number | null {
    const currentFacilityBed = this.currentFacilityBed();
    if (!currentFacilityBed.emptyBedReasons) {
      return null;
    }

    const reasonValue = currentFacilityBed.emptyBedReasons[reasonKey] as any;
    if (
      reasonValue &&
      typeof reasonValue === 'object' &&
      'current' in reasonValue
    ) {
      return reasonValue.current;
    }
    return null;
  }

  getLastWeekValue(reasonKey: keyof EmptyBedReasonsDto): number | null {
    const currentFacilityBed = this.currentFacilityBed();
    if (!currentFacilityBed.emptyBedReasons) {
      return null;
    }

    const reasonValue = currentFacilityBed.emptyBedReasons[reasonKey] as any;
    if (
      reasonValue &&
      typeof reasonValue === 'object' &&
      'previous' in reasonValue
    ) {
      return reasonValue.previous;
    }
    return null;
  }

  // Show/hide the has empty beds radio buttons
  // Show/hide empty bed reasons section
  isForEmptyBedsReasons() {
    return this.displayFor() === 'emptyBedReasons';
  }

  async onEmptyBedReasonBlur($event: GoaInputOnBlurDetail) {
    this._facilityEntryBedRowStateService.setBedRowValidation = {
      bedType: this.bedType(),
      isValid: this.formGroup.valid,
    };
    await this._facilityEntryBedRowStateService.saveEmptyBedReasons(
      this.bedType(),
      $event.name as keyof EmptyBedReasonsDto,
      Number($event.value)
    );
  }

  onEmptyBedReasonChange(
    reasonKey: keyof EmptyBedReasonsDto,
    $event: GoabInputOnChangeDetail
  ) {
    const stringValue = $event.value as string;
    const newValue =
      stringValue && stringValue.trim() !== '' ? Number(stringValue) : null;

    this._facilityEntryBedRowStateService.updateEmptyBedReason(
      this.bedType(),
      reasonKey,
      newValue
    );
  }

  async onHasEmptyBedsChange($event: GoabRadioGroupOnChangeDetail) {
    if ($event && $event.value) {
      const newValue = $event.value === 'true';
      this._facilityEntryBedRowStateService.setBedRowValidation = {
        bedType: this.bedType(),
        isValid: this.formGroup.valid,
      };
      await this._facilityEntryBedRowStateService.updateHasEmptyBeds(
        this.bedType() as BedTypeCode,
        newValue
      );
    }
  }
}
